import axios, { CancelToken } from 'client';
import { AxiosResponse } from 'axios';
import * as dto from 'client/dto';

const axiosInstance = axios;

let getDiseaseRiskByLocationCancel = null;

export type getDiseaseCaseCountAPI = (options: {
  diseaseId: number;
  locationId: number;
  caseCountStartDate: string;
  caseCountEndDate?: string;
  includeGlobalCaseCounts?: boolean;
}) => Promise<AxiosResponse<dto.HierarchicalCaseCountModel>>;

const headers = {
  'X-Entity-Type': 'Disease',
};

function getDiseaseRiskByLocation(options: {
  geonameId: number;
  caseCountStartDate: string;
}): Promise<AxiosResponse<dto.RiskAggregationModel>> {
  const { geonameId, caseCountStartDate } = options;
  getDiseaseRiskByLocationCancel && getDiseaseRiskByLocationCancel();
  return axios.get(`/api/diseaserisk`, {
    params: {
      caseCountStartDate,
      geonameId,
    },
    cancelToken: new CancelToken(c => {
      getDiseaseRiskByLocationCancel = c;
    }),
    headers,
  });
}

function getAllDiseases(): Promise<dto.DiseaseInformationModel[]> {
  return axios
    .get(`/api/disease`, {
      headers: {
        'X-Entity-Type': 'Diseases',
      },
    })
    .then(({ data }) => data);
}

function getDisease(diseaseId: number): Promise<dto.DiseaseInformationModel> {
  return axios.get(`/api/disease/${diseaseId}`, { headers }).then(({ data }) => data);
}

function getDiseaseCaseCount(options: {
  diseaseId: number;
  locationId: number;
  caseCountStartDate: string;
  caseCountEndDate?: string;
  includeGlobalCaseCounts?: boolean;
}): Promise<AxiosResponse<dto.HierarchicalCaseCountModel>> {
  const { diseaseId, locationId, caseCountStartDate, caseCountEndDate, includeGlobalCaseCounts } =
    options;

  return axios.get(`/api/disease/${diseaseId}/casecount`, {
    params: {
      diseaseId,
      locationId,
      caseCountStartDate,
      caseCountEndDate,
      includeGlobalCaseCounts,
    },
  });
}

/** @deprecated */
function getDiseaseCaseCountProximal(options: {
  diseaseId: number;
  locationId: number;
  caseCountStartDate: string;
  caseCountEndDate?: string;
}): Promise<AxiosResponse<dto.ProximalCaseCountModel[]>> {
  const { diseaseId, locationId, caseCountStartDate, caseCountEndDate } = options;

  return axios.get(`/api/disease/${diseaseId}/casecount/proximal`, {
    params: {
      diseaseId,
      locationId,
      caseCountStartDate,
      caseCountEndDate,
    },
  });
}

function getDiseaseGroups(): Promise<dto.DiseaseGroupModel[]> {
  return axios
    .get(`/api/disease/groups`, {
      headers: {
        'X-Entity-Type': 'Disease Groups',
      },
    })
    .then(({ data }) => data);
}

function DiseaseController_GetTemporalActivity(
  model: dto.TemporalActivityRequestModel
): Promise<dto.TemporalActivityModel[]> {
  return axios.post(`/api/disease/${model.diseaseId}/temporal`, model).then(({ data }) => data);
}

function DiseaseRiskController_GetTemporalActivity(
  model: dto.TemporalActivityRequestModel
): Promise<dto.DiseaseTemporalActivityModel[]> {
  return axiosInstance.post(`/api/diseaserisk/temporal`, model).then(({ data }) => data);
}

function DiseaseActivityController_GetTemporalActivityForDiseaseActivity(
  diseaseId: number,
  speciesId: number,
  model: dto.TemporalActivityRequestModel
): Promise<dto.CountryTemporalActivityModel[]> {
  return axiosInstance
    .post(`/api/activity/disease/${diseaseId}/species/${speciesId}/temporal`, model, {
      params: { diseaseId, speciesId },
    })
    .then(({ data }) => data);
}

function getLocationRiskSummary(
  model: dto.LocationRiskSummaryRequestModel,
  client = axios
): Promise<Record<number, dto.DiseaseLocationMatrixModel>> {
  return client.post(`/api/disease/locationrisks`, model).then(({ data }) => data);
}

function getDiseaseRiskImportationSources(
  model: { diseaseActivityId: number; locationId: number },
  client = axios
): Promise<dto.GetGeonameModel[]> {
  return client.post(`/api/diseaserisk/importationsources`, model).then(({ data }) => data);
}

function DiseaseController_GetDiseaseActivityDescriptions(
  model: dto.DiseaseActivityDescriptionsRequestModel
): Promise<dto.DiseaseActivityDescriptionsModel> {
  return axiosInstance
    .post(`/api/disease/${model.diseaseId}/description`, model)
    .then(({ data }) => data);
}

// DiseaseApi
export default {
  getDiseaseRiskByLocation,
  getAllDiseases,
  getDisease,
  getDiseaseCaseCount,
  getDiseaseCaseCountProximal,
  getDiseaseGroups,
  getLocationRiskSummary,
  DiseaseController_GetTemporalActivity,
  DiseaseRiskController_GetTemporalActivity,
  DiseaseActivityController_GetTemporalActivityForDiseaseActivity,
  getDiseaseRiskImportationSources,
  DiseaseController_GetDiseaseActivityDescriptions,
};
